import React, {useState, useCallback} from 'react';
import {Table, Button} from 'antd';
import {FolderOutlined, DownOutlined, RightOutlined, DownloadOutlined} from '@ant-design/icons';
import {TreeNode} from '../../../../store/modelFile';
import type {ColumnsType} from 'antd/es/table';
import {getFileIcon} from '../../../../utils/fileIcons';
import styles from './FileTreeNode.module.scss';

interface FileTreeNodeProps {
  files: TreeNode[];
  node?: TreeNode;
  isRoot?: boolean;
  defaultFolderOpen?: boolean;
  onDownloadFile: (fileUuid: string, fileName: string) => void;
  level?: number;
}

interface FileRowData extends TreeNode {
  updatedOn?: Date;
  updatedBy?: string;
  fileTag?: string;
  children?: TreeNode[];
}

/**
 * FileTreeNode component - renders a folder with nested files in a table
 * For regular models only - no THEOREM CDD access control
 */
export const FileTreeNode: React.FC<FileTreeNodeProps> = ({
  files,
  node,
  isRoot = false,
  defaultFolderOpen = true,
  onDownloadFile,
  level = 0,
}) => {
  const [isOpen, setIsOpen] = useState(isRoot || defaultFolderOpen === true);

  const handleToggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  // Determine which data to work with
  let directories: TreeNode[] = [];
  let childrenFiles: FileRowData[] = [];
  let folderName = '';

  if (isRoot) {
    // Root level: only include top-level items (path has no '/').
    // The flat array from flattenTree contains every nested node; nested folders
    // and files must render only via their parent folder's children property.
    files.forEach((item) => {
      if (item.folder && !item.path.includes('/')) {
        directories.push(item);
      } else if (!item.folder && !item.path.includes('/')) {
        childrenFiles.push(item as FileRowData);
      }
    });
  } else if (node) {
    // Subfolder: use the children array from the node
    folderName = node.name;
    const nodePath = (node as any).path || node.name;
    const nodeChildren = (node as any).children as TreeNode[] | undefined;

    if (nodeChildren) {
      // Filter to get only IMMEDIATE children (not all descendants)
      // Immediate children have path = parentPath/childName (only one level deeper)
      const immediateChildren = nodeChildren.filter((item: any) => {
        const itemPath = item.path || item.name;
        // Remove the parent path prefix to see the relative path
        const relativePath = itemPath.startsWith(nodePath)
          ? itemPath.substring(nodePath.length).replace(/^\//, '')
          : itemPath;
        // Immediate child has no '/' in relative path (not nested deeper)
        return !relativePath.includes('/') || relativePath === item.name;
      });

      immediateChildren.forEach((item) => {
        if (item.folder) {
          directories.push(item);
        } else {
          childrenFiles.push(item as FileRowData);
        }
      });
    }
  }

  // Sort folders and files alphabetically by name
  directories.sort((a, b) => a.name.localeCompare(b.name));
  childrenFiles.sort((a, b) => a.name.localeCompare(b.name));

  // Ant Design table columns - no Access column for regular models
  const columns: ColumnsType<FileRowData> = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      width: '45%',
      render: (name: string) => (
        <span className={styles.fileName}>
          {getFileIcon(name, styles.fileIcon)}
          {name}
        </span>
      ),
    },
    {
      title: 'Updated On',
      dataIndex: 'updatedOn',
      key: 'updatedOn',
      render: (date: Date | string) => {
        if (!date) return '';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
    },
    {
      title: 'File Type',
      dataIndex: 'fileTag',
      key: 'fileTag',
    },
    {
      title: 'Download',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_: any, record: FileRowData) => (
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onDownloadFile(record.uuid, record.name);
          }}
          className={styles.downloadButton}
        />
      ),
    },
  ];

  const ArrowIcon = isOpen ? DownOutlined : RightOutlined;

  return (
    <>
      {/* Folder header (not for root) */}
      {!isRoot && node && (
        <button type="button" className={styles.folderHeader} onClick={handleToggleOpen} data-level={level}>
          <FolderOutlined className={styles.folderIcon} />
          <span className={styles.folderName}>{folderName}</span>
          <ArrowIcon className={styles.arrowIcon} />
        </button>
      )}

      {/* Expanded folder content */}
      {isOpen && (
        <div
          className={isRoot ? undefined : styles.folderContent}
          data-level={level}
          data-single-child={directories.length === 1 && childrenFiles.length === 0 ? 'true' : undefined}
        >
          {/* Nested folders */}
          {directories.map((childNode) => (
            <FileTreeNode
              key={childNode.uuid}
              files={files}
              node={childNode}
              defaultFolderOpen={defaultFolderOpen}
              onDownloadFile={onDownloadFile}
              level={level + 1}
            />
          ))}

          {/* Files table */}
          {childrenFiles.length > 0 && (
            <Table
              dataSource={childrenFiles}
              columns={columns}
              rowKey="uuid"
              pagination={false}
              size="small"
              className={styles.filesTable}
            />
          )}
        </div>
      )}
    </>
  );
};
